import {
  ZoneDTO,
  PagedResultDTOOfZoneDTO,
  ZoneClient,
  CreateZoneDTO,
  UpdateZoneDTO,
  ZoneDeviceDTO,
  FilterDTO,
  SortDirection,
  FilterOperator
} from 'src/app/base/models/models.swagger.generated';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ToastService } from 'src/app/base/services/toast.service';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { tap, catchError } from 'rxjs/operators';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable()
export class ZoneDetailsService {
  public totalNumberOfZones: number;
  public clientId: number;
  public zoneDevices: ZoneDeviceDTO[];
  public isBusy = false;

  public loadedZone: ZoneDTO;

  constructor(private zoneClient: ZoneClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService) {
  }

  public reset(): void {
    this.isBusy = false;
  }

  // eslint-disable-next-line max-len
  public getZones(clientId: number, filters: FilterDTO[], skip: number, take: number, sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfZoneDTO> {
    this.spinnerService.show();
    this.clientId = clientId;

    if (!sortBy) {
      sortBy = 'name';
    }

    return this.zoneClient.searchZones(this.clientId, filters, skip, take, sortBy, sortDirection)
      .pipe(
        tap(pagedResult => {
          this.totalNumberOfZones = pagedResult.totalNumberOfItems;
          this.spinnerService.hide();
        }),
        catchError((error: any) => {
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return of(error);
        })
      );
  }

  public getZone(zoneId: number): Observable<ZoneDTO> {
    if (!zoneId) {
      this.loadedZone = new ZoneDTO();
      return of(this.loadedZone);
    }
    this.loadedZone = null;
    this.spinnerService.show();
    return this.zoneClient.getZone(zoneId).pipe(
      tap((zone: ZoneDTO) => {
        this.loadedZone = zone;
        this.spinnerService.hide();
      }),
      catchError((error: any) => {
        this.spinnerService.hide();
        return of(null);
      })
    );
  }

  public createZone(createZoneDTO: CreateZoneDTO): Observable<ZoneDTO> {
    this.spinnerService.show();
    this.isBusy = true;
    return this.zoneClient.createZone(createZoneDTO)
      .pipe(
        tap(() => {
          this.isBusy = false;
          this.spinnerService.hide();
          this.toastService.showSuccess(
            'i18n.domain.administration.zone-details.messages.add-title',
            'i18n.domain.administration.zone-details.messages.add-success-message'
          );
        }),
        catchError((error: any) => {
          this.isBusy = false;
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return of(error);
        })
      );
  }

  public updateZone(updateZoneDTO: UpdateZoneDTO): Observable<any> {
    this.spinnerService.show();
    this.isBusy = true;
    return this.zoneClient.updateZone(updateZoneDTO)
      .pipe(
        tap(() => {
          this.isBusy = false;
          this.spinnerService.hide();
          this.toastService.showSuccess(
            'i18n.domain.administration.zone-details.messages.update-title',
            'i18n.domain.administration.zone-details.messages.update-success-message'
          );
        }),
        catchError((error: any) => {
          this.isBusy = false;
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.handleErrorResponse(error);
          }
          return of(error);
        })
      );
  }

  public deleteZone(id: number): Observable<any> {
    this.isBusy = true;
    this.spinnerService.show();
    return this.zoneClient.deleteZone(id)
      .pipe(
        tap(() => {
          this.isBusy = false;
          this.spinnerService.hide();
          this.toastService.showSuccess(
            'i18n.domain.administration.zone-details.messages.delete-title',
            'i18n.domain.administration.zone-details.messages.delete-success-message'
          );
        }),
        catchError((error: any) => {
          this.isBusy = false;
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return of(error);
        })
      );
  }

  public setCurrentParkerCount(id: number, parkerCount: number): Observable<ZoneDTO> {
    return this.zoneClient.setCurrentParkerCount(id, parkerCount)
      .pipe(
        catchError((error: any) => {
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return of(error);
        })
      );
  }

  private handleErrorResponse(error: any) {
    if (error && error.response) {
      try {
        const errorObject = JSON.parse(error.response);
        if (errorObject.ErrorCode) {
          this.toastService.showError('i18n.errors.server-error.title',
            'i18n.domain.request-management.errors.' + errorObject.ErrorCode,
            null, ErrorType.serverError);
        }
        else {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message',
            null, ErrorType.serverError);
        }
      }
      catch {
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message',
          null, ErrorType.serverError);
      }
    }
    else {
      this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message',
        null, ErrorType.serverError);
    }
  }

  public searchZone(searchText: string): Observable<PagedResultDTOOfZoneDTO> {

    var filters: FilterDTO[] = [];
    // if searchText is a number, then add Id filter, else name filter
    if (!isNaN(Number(searchText))) {
      filters.push(new FilterDTO({ isComplex: false, field: "id", operator: FilterOperator.Equal, value: searchText }));
    } else {
      filters.push(new FilterDTO({ isComplex: false, field: "name", operator: FilterOperator.Contains, value: searchText }));
    }

    return this.zoneClient.searchZones(0, filters, 0, 100, "name", SortDirection.Ascending)
      .pipe(
        tap(pagedResult => {
          return of(pagedResult);
        }),
        catchError((error: any) => {
          this.spinnerService.hide();
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return of(error);
        })
      );
  }
}
